import { useEffect, useRef, useState, useCallback } from 'react';
import {
  LayoutGrid, List as ListIcon, ChevronRight, RotateCcw, Trash2,
  ArrowUpDown, Info, Rows3 as Rows, Lock, Check,
} from '../../icons';
import { layerConfirm, layerAlert, layerPrompt } from '../../components/LayerPopup/LayerPopup';
import {
  listFolderChildren,
  listTrash, restoreFromTrash, purgeFromTrash, emptyTrash,
  getFileUsage, addShare, removeShare, listShares,
  type NodePublic, type StorageUsage, type TrashNode, type UsageSource,
} from '../../api/workspace';
import { useAuthStore } from '../../store/authStore';
import type { FileKind } from '../../lib/file-kind';
import { createBlankDocument, type NewDocKind } from '../../lib/workspace-new-doc';
// 화면 머리 — 근태 · 메일과 같은 정본 A3 (2026-09-08).
import { PageHeader } from '../../components/shared/PageHeader';
import { WorkspaceFilterBar } from './WorkspaceFilterBar';
import { WorkspaceSidebar, type WorkspaceView } from './WorkspaceSidebar';
import type { CreateAction } from './WorkspaceCreateMenu';
import { WorkspaceFileIcon } from './WorkspaceFileIcon';
import { useDragSelect } from '../../hooks/useDragSelect';
import { WorkspaceTable, type ViewMode } from './WorkspaceTable';
import { WorkspacePagination, WORKSPACE_PAGE_SIZES } from './WorkspacePagination';
import { WorkspaceRowMenu, type RowAction } from './WorkspaceRowMenu';
import { WorkspaceSortMenu, type SortField, type SortOrder } from './WorkspaceSortMenu';
import { WorkspaceSelectionBar, type BulkAction } from './WorkspaceSelectionBar';
import { WorkspaceTrashBar } from './WorkspaceTrashBar';
import { WorkspaceDetailPanel } from './WorkspaceDetailPanel';
import { WorkspaceShareModal } from './WorkspaceShareModal';
import { WorkspaceFolderPicker } from './WorkspaceFolderPicker';
import { WorkspaceColorModal } from './WorkspaceColorModal';
import { WorkspacePopover, PopoverItem } from './WorkspacePopover';
import { WorkspaceDeleteWarnModal, type DeleteTarget } from './WorkspaceDeleteWarnModal';
import { useWorkspaceActions } from './useWorkspaceActions';
import { FolderCreateModal } from './FolderCreateModal';
import { FileUploadModal } from './FileUploadModal';
import { hasExternalFiles, readExternalFiles } from '../../lib/workspace-dnd';
import { StorageManageModal } from './StorageManageModal';
// 2026-08-12 — 메타데이터만 보여주던 WorkspaceFileDetail 을 2계층 문서 패널로 교체.
//   티어1 경량 미리보기(기존 채팅 렌더러 재사용) + '편집' 시 티어2 지연 로드.
import { WorkspaceDocPanel } from './WorkspaceDocPanel';
import { useModalLeave } from '../../hooks/useModalLeave';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { tWorkspace, type WorkspaceI18nKey } from '../../lib/workspace-i18n';
import './WorkspacePage.css';

/**
 * 브레드크럼 뿌리의 이름 — **화면마다 다르다** (2026-08-14 2차).
 *
 * 어느 화면에서나 '워크스페이스' 를 쓰면 회사 문서·공유됨에서 폴더로 들어갔을 때
 * `워크스페이스 > 규정` 이 되어 그 폴더가 내 파일에 있는 것처럼 읽힌다.
 * 누르면 **그 화면의 최상위**로 돌아가므로(handleBreadcrumbJump(-1)) 이름도 그 화면이어야 한다.
 */
/**
 * 화면 머리의 **한 줄 설명** (정본 A3 · 2026-09-08).
 * ★ 「무엇을 보는 곳인가」만 적는다 — 규칙(끌어 옮기기 · 휴지통)은 **도움말**로 간다(P8).
 */
const VIEW_DESC_KEY: Record<WorkspaceView, WorkspaceI18nKey> = {
  home: 'workspace.head.desc_home',
  my: 'workspace.head.desc_my',
  shared: 'workspace.head.desc_shared',
  'shared-by-me': 'workspace.head.desc_shared_by_me',
  company: 'workspace.head.desc_company',
  trash: 'workspace.head.desc_trash',
};

const ROOT_LABEL_KEY: Record<WorkspaceView, WorkspaceI18nKey> = {
  home: 'workspace.nav.home',
  my: 'workspace.nav.my',
  shared: 'workspace.nav.shared_with_me',
  'shared-by-me': 'workspace.nav.shared_by_me',
  company: 'workspace.nav.company',
  trash: 'workspace.nav.trash',
};

/**
 * WorkspacePage (P3-FILE-FE-2) — 시안 line 2421~2531 매핑.
 *
 * 구조:
 *   - 헤더: 제목 + 새 파일 버튼 (업로드는 후속).
 *   - 스토리지 사용량 카드 (placeholder — 후속에서 backend storage_quota endpoint 후 연동).
 *   - 좌측 좁은 폴더 트리 (재귀 expand) + 우측 그리드/리스트 (브레드크럼 + 토글).
 */
export function WorkspacePage() {
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);  // null = root
  const [breadcrumb, setBreadcrumb] = useState<NodePublic[]>([]);
  const breadcrumbRef = useRef<NodePublic[]>([]);
  breadcrumbRef.current = breadcrumb;
  const [children, setChildren] = useState<NodePublic[]>([]);
  const [creating, setCreating] = useState(false);
  // 2026-08-12 — 파일 업로드 모달.
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * 들어가려던 폴더가 404(권한 없음·사라짐)였다는 알림 (2026-10-27 사용자 지시).
   * `error` 와 따로 둔다 — 한 단계 위로 되돌리면 `refresh` 가 `error` 를 지우기 때문이다.
   */
  const [folderDenied, setFolderDenied] = useState<string | null>(null);
  // design-20260717 Phase 1 — ④ 우측 상세(파일 상세) 선택 파일. null = 패널 닫힘.
  const [selectedFile, setSelectedFile] = useState<NodePublic | null>(null);
  // 2026-08-12 — 유형 필터 + 검색 + 페이지네이션 + 실제 사용량.
  const [kind, setKind] = useState<FileKind>('all');
  const [query, setQuery] = useState('');
  /**
   * 폴더 구획 (2026-08-21) — 페이지와 무관하게 목록 맨 위에 고정된다.
   *
   * 서버가 폴더를 파일과 따로 뽑아 준다. 예전에는 한 흐름에 섞여 있어서, 첨부가 많은
   * 계정은 첫 페이지에 폴더가 한 건도 안 실렸다(좌측 트리까지 함께 비었다).
   * 폴더 경계가 없는 범위(홈·공유됨·검색)에서는 빈 배열이고, 그때 폴더는 예전처럼
   * `children` 안에 섞여 온다.
   */
  const [folderSection, setFolderSection] = useState<NodePublic[]>([]);
  /**
   * 번호 페이지네이션 (2026-08-21) — '더 보기' 를 대체한다.
   *
   * 서버는 정렬을 지정하면 `cursor` 를 **offset 으로** 해석하므로(paginateByOffset),
   * `cursor = (page-1) * pageSize` 로 임의 페이지 점프가 된다. 총 건수는 응답의 `total`.
   * 기본 정렬이 '수정일 최신순' 이라 실사용에서 정렬이 빠지는 경로는 없다.
   */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(WORKSPACE_PAGE_SIZES[0]);
  const [total, setTotal] = useState(0);
  const [usage, setUsage] = useState<StorageUsage | null>(null);
  const searching = query !== '';

  // 2026-08-14 드라이브화 — 좌측 네비의 현재 화면.
  //   home/my/shared 는 같은 목록 API 를 scope 만 바꿔 부르고,
  //   trash 는 별도 엔드포인트, company 는 회사 폴더로 진입한 뒤 my 와 같은 화면이 된다.
  const [view, setView] = useState<WorkspaceView>('my');
  const [trashItems, setTrashItems] = useState<TrashNode[]>([]);
  // 폴더 업로드는 같은 모달을 webkitdirectory 로 여는 것뿐이다.
  const [uploadDirectory, setUploadDirectory] = useState(false);

  // ── 드라이브 표 (2026-08-14) ────────────────────────────────────────────
  const [tableView, setTableView] = useState<ViewMode>('list');
  /*
   * 초기 정렬 = **수정일 최신순** (2026-08-19).
   *
   * 예전 기본은 '정렬 미지정' 이었고, 그러면 서버가 `type asc → name asc` 로 준다. 그런데
   * type 어휘가 'chat-attachment' · 'folder' · 'workspace-file' 이라 알파벳순이 곧
   * **채팅첨부가 폴더보다 위** 였다 — 방금 올린 파일을 찾으려면 이름순 목록을 훑어야 했다.
   *
   * 축을 명시하면 서버가 커서 페이징 대신 offset 페이징으로 갈아탄다(workspace.routes.ts
   * `paginateByOffset = sort !== null`). 그래서 '더 보기' 의 페이지 경계도 함께 안전해진다
   * — 정렬 미지정 경로는 id 커서와 묶여 있어 홈 2페이지에서 행이 어긋날 수 있었다.
   */
  const [sortField, setSortField] = useState<SortField | null>('updated');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  /**
   * 체크 하나를 **정한다**(토글 아님) — 끌어서 여러 개 켜고 끄기가 쓴다 (2026-08-18).
   * 같은 값으로 여러 번 불려도 상태가 안 바뀌게 미리 걸러 낸다(끌 때마다 재렌더 방지).
   */
  const setSelectOne = useCallback((id: string, on: boolean) => {
    setSelected((cur) => {
      if (cur.has(id) === on) return cur;
      const next = new Set(cur);
      on ? next.add(id) : next.delete(id);
      return next;
    });
  }, []);

  /** 휴지통 목록은 표(WorkspaceTable)를 안 쓰므로 여기서 따로 같은 끌기를 붙인다. */
  const trashDrag = useDragSelect(setSelectOne);
  const [detailOpen, setDetailOpen] = useState(false);
  /** 열려 있는 팝오버 — 앵커 요소를 들고 있어야 위치를 잡는다. */
  const [rowMenu, setRowMenu] = useState<{ node: NodePublic; anchor: HTMLElement } | null>(null);
  const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null);
  const [viewAnchor, setViewAnchor] = useState<HTMLElement | null>(null);
  /** 모달 — 대상 노드를 함께 들고 연다. */
  const [shareTarget, setShareTarget] = useState<{ node: NodePublic; tab: 'add' | 'manage' } | null>(null);
  const [pickerTarget, setPickerTarget] = useState<{ nodes: NodePublic[]; mode: 'move' | 'copy' } | null>(null);
  const [colorTarget, setColorTarget] = useState<NodePublic | null>(null);

  // 스마트 폴더 — 내 파일 루트에서 출처별로 묶어 보여 준다(가상 폴더).
  const [smartFolders, setSmartFolders] = useState<Array<{ source: UsageSource; count: number }>>([]);
  const [smartSource, setSmartSource] = useState<UsageSource | null>(null);
  /** 삭제 전 사용처 경고 — 참조가 있는 대상만 담긴다. */
  const [deleteWarn, setDeleteWarn] = useState<
    { targets: DeleteTarget[]; clean: NodePublic[] } | null>(null);

  /**
   * 이름을 눌러 **들여다보는 중인** 항목 (2026-08-14).
   *
   * 체크(`selected`)와 분리한다 — 이름을 누르면 체크까지 되는 바람에, 하나 열어 보려던 것이
   * 곧 일괄 선택이 되어 상단 줄이 액션 툴바로 바뀌었다. 체크는 **여러 개를 골라 한꺼번에
   * 처리하려는 의사표시**이고, 이름 클릭은 **하나를 보려는 것**이라 뜻이 다르다.
   */
  const [focusedId, setFocusedId] = useState<string | null>(null);

  const clearSelection = useCallback(() => setSelected(new Set()), []);
  /** 회사 문서 화면에서 일반 사원은 읽기 전용이다(등록·해제는 관리자만). */
  const [companyReadOnly, setCompanyReadOnly] = useState(false);
  /*
   * 바깥(파일 탐색기)에서 끌어다 놓기 (2026-08-19).
   *
   * 여태 워크스페이스에는 외부 파일 드롭 처리가 없었고, window/document 레벨 기본 동작
   * 차단도 없었다. 그래서 목록에 파일을 놓으면 브라우저가 그 파일을 그냥 열어 버렸다
   * (사용자 눈에는 "업로드가 아니라 다운로드"). 받아서 업로드 모달로 넘긴다.
   */
  const [dropFiles, setDropFiles] = useState<readonly File[]>([]);
  const [uploadTarget, setUploadTarget] = useState<{ id: string | null; name: string } | null>(null);
  const [fileOver, setFileOver] = useState(false);
  /** 용량 관리 모달 (2026-08-14 2차) — 좌측 하단 '용량관리'. */
  const [storageOpen, setStorageOpen] = useState(false);
  /**
   * 좌측 폴더 트리 갱신 신호 (2026-08-14).
   *
   * 트리는 mount 시 1회만 조회하고 캐시한다. 그래서 폴더를 만들거나 지워도 **본문 표만
   * 바뀌고 좌측은 그대로**였다 — "폴더를 만들었는데 바로 반영이 안 된다" 의 정체다.
   * 목록을 바꾸는 동작마다 이 값을 올려 트리가 다시 읽게 한다.
   */
  const [treeVersion, setTreeVersion] = useState(0);
  /**
   * 목록 요청 일련번호 — **늦게 도착한 옛 응답이 새 화면을 덮어쓰는 것**을 막는다.
   *
   * 회사공유로 전환할 때 실제로 그랬다: 화면 전환이 먼저 '내 파일 루트' 조회를 띄우고,
   * 그 응답(226ms)이 회사 목록 응답(13ms)보다 늦게 도착해 **회사공유 화면에 내 파일이
   * 그대로 남았다.** 응답을 받을 때 자기 차례인지 확인한다.
   */
  const listSeqRef = useRef(0);
  /**
   * 회사공유에 등록·해제할 수 있는 사람인가 (2026-08-14).
   *
   * 회사공유는 **관리자가 등록한 것만** 보이므로, 등록 메뉴도 관리자에게만 보인다.
   * 최종 판정은 서버가 한다(일반 사원이 건 전사 공유는 회사공유 목록에 안 들어온다).
   */
  const isAdmin = useAuthStore((s) => s.user?.role === 'ADMIN' || s.user?.role === 'SYSTEM_ADMIN');

  const {
    shouldRender: shouldRenderCreate,
    isLeaving: isCreateLeaving,
  } = useModalLeave(creating, 200);
  const {
    shouldRender: shouldRenderUpload,
    isLeaving: isUploadLeaving,
  } = useModalLeave(uploading, 200);
  useBodyScrollLock(shouldRenderCreate || shouldRenderUpload);

  /**
   * 회사 문서의 **최상위**인가 (2026-08-14 2차 — 판정이 단순해졌다).
   *
   * 예전에는 "회사 공유 폴더 안" 도 최상위로 쳐야 해서 폴더 id 를 기다리는지 여부가
   * 조건에 섞여 있었다. 이제 회사 문서는 **전사 공유된 항목의 모음**이지 특정 폴더가
   * 아니므로, 폴더로 들어가지 않은 상태가 곧 최상위다.
   */
  const atCompanyTop = view === 'company' && currentFolderId === null;

  /**
   * 만들기·업로드가 들어갈 폴더 (2026-08-14 → 2차 개정).
   *
   * 회사 문서 최상위에서 만들면 **내 파일 루트**에 만든다(사용자 요구: "폴더를 만들면 내 파일
   * 메뉴에서 보일 수 있도록"). 예전에는 회사 공유 폴더 안에 만들었는데, 그 폴더는 목록 조회
   * 규칙과 만들기 대상이 서로 다른 상태를 만들어 화면이 오갔고, 만든 사람은 자기가 만든 것을
   * '내 파일' 에서 찾지 못했다. 회사에 내보이려면 행 메뉴의 '회사공유에 등록' 을 건다.
   */
  /** 업로드를 켤 수 있는 상태인가 — 읽기 전용 회사문서·휴지통에서는 받지 않는다. */
  const canUpload = !companyReadOnly && view !== 'trash';

  /** 끌어다 놓은 파일로 업로드 모달을 연다. target 을 안 주면 지금 보고 있는 폴더. */
  const openUploadWithFiles = useCallback((files: File[], target?: { id: string | null; name: string }) => {
    if (files.length === 0) return;
    setDropFiles(files);
    setUploadTarget(target ?? null);
    setUploadDirectory(false);
    setUploading(true);
  }, []);

  /*
   * 워크스페이스 화면에 있는 동안에는 **어디에 놓아도** 브라우저가 파일을 열지 않게 한다.
   * 드롭존 밖(툴바·여백 등)에 놓쳐 떨어뜨렸을 때 페이지가 통째로 그 파일로 바뀌는 것을 막는다.
   * 우리 드롭존은 React 핸들러가 먼저 받아 처리하므로 이 가드와 겹치지 않는다.
   */
  useEffect(() => {
    function block(e: DragEvent) {
      if (!e.dataTransfer || !Array.from(e.dataTransfer.types).includes('Files')) return;
      e.preventDefault();
    }
    window.addEventListener('dragover', block);
    window.addEventListener('drop', block);
    return () => {
      window.removeEventListener('dragover', block);
      window.removeEventListener('drop', block);
    };
  }, []);

  const createParentId = atCompanyTop ? null : currentFolderId;
  const createParentName = atCompanyTop
    ? tWorkspace('workspace.nav.my')
    : (breadcrumb[breadcrumb.length - 1]?.name ?? tWorkspace('workspace.title'));

  const refresh = useCallback(async () => {
    const seq = ++listSeqRef.current;
    /** 이 응답이 아직 화면의 주인인가 — 아니면 조용히 버린다. */
    const isStale = () => seq !== listSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      if (view === 'trash') {
        // 휴지통은 목록 API 와 스키마가 달라(삭제 시각) 별도 엔드포인트를 쓴다.
        const items = await listTrash();
        if (isStale()) return;
        setTrashItems(items);
        setFolderSection([]);
        setTotal(items.length);
        return;
      }
      const res = await listFolderChildren(currentFolderId ?? 'root', {
        q: query,
        kind,
        // 회사공유 최상위는 폴더 진입이 아니라 전용 범위다 — 회사 폴더 직속 자식 +
        // 관리자가 자기 '내 파일' 에서 등록한 것. 그 안의 하위 폴더로 들어가면 folder 범위.
        // 스마트 폴더를 열었으면 출처 범위가 우선한다.
        /*
         * ★ 홈·공유됨·내가 공유함에서 **폴더 안으로 들어가면 'folder'** 다 (2026-10-27 사용자 지시).
         *   그 셋은 서버가 폴더 경계를 무시하는(flat) 범위라, 들어가도 `parentId` 가 버려지고
         *   **같은 최상위 목록이 다시** 왔다 — 공유 폴더 안(하위 폴더·파일)이 영영 안 보였고,
         *   같은 줄을 또 누를 때마다 경로가 `… > A > A > A` 로 늘었다.
         *   폴더 안 권한은 서버 `resolveAccess`(조상까지 본다)가 판정한다 — 회사 문서의 atCompanyTop 과 같은 꼴.
         */
        scope: smartSource !== null ? 'source'
          : currentFolderId !== null && (view === 'home' || view === 'shared' || view === 'shared-by-me') ? 'folder'
          : view === 'home' ? 'home'
            : view === 'shared' ? 'shared'
              : view === 'shared-by-me' ? 'shared-by-me'
                : atCompanyTop ? 'company' : 'folder',
        ...(smartSource !== null ? { source: smartSource } : {}),
        // 기본이 '수정일 최신순' 이라 평소에는 늘 실려 나간다. null 은 사용자가 축을 지운 경우뿐.
        ...(sortField ? { sort: sortField, order: sortOrder } : {}),
        /*
         * 번호 페이지 (2026-08-21). 정렬을 지정했을 때 서버는 `cursor` 를 **offset 으로**
         * 해석하므로 그대로 쪽 번호로 환산한다. 1쪽은 안 보낸다(옛 서버와 같은 요청).
         */
        limit: pageSize,
        ...(page > 1 ? { cursor: String((page - 1) * pageSize) } : {}),
      });
      if (isStale()) return;
      /*
       * 폴더 구획을 갈라 담는다 (2026-08-21).
       *
       * 서버가 `folders` 를 보냈을 때만 가른다 — 안 보내는 범위(홈·공유됨·검색)와 옛
       * 서버에서는 예전처럼 폴더가 `children` 안에 섞인 채로 둔다(회귀 0).
       */
      const section = Array.isArray(res.folders) ? res.folders : null;
      setFolderSection(section ?? []);
      setChildren(section && section.length > 0
        ? res.children.filter((c) => c.type !== 'folder')
        : res.children);
      /*
       * 총 건수 폴백 — 옛 서버는 `total` 을 안 준다 (2026-08-21).
       *
       * 그때 `children.length` 를 쓰면 한 쪽 분량(50)이 곧 총계가 되어 **페이저가 사라지고
       * 첫 50건만 보이는** 상태가 된다. 옛 서버도 offset 페이징 자체는 되므로,
       * "다음이 있다"(nextCursor)를 근거로 **최소한 한 쪽 더 있다**고 세어 페이저를 살린다.
       * 숫자는 실제보다 작을 수 있지만 넘길 수는 있다 — 서버가 올라가면 정확해진다.
       */
      const seen = (page - 1) * pageSize + res.children.length;
      setTotal(res.total ?? (res.nextCursor ? seen + 1 : seen));
      if (res.usage) setUsage(res.usage);
      /*
       * 스마트 폴더 목록은 **내 파일 루트에서만** 온다(서버 조건: scope=folder · parentId=null ·
       * 검색 아님). 그래서 응답을 그대로 받아쓰면, 출처를 하나 고르는 순간(scope=source) 빈
       * 배열이 와서 목록이 통째로 사라졌다 — 좌측에서 다른 출처로 갈아타거나 '전체' 로
       * 되돌아갈 방법이 없어진다. 목록이 실린 응답일 때만 갱신하고 아니면 들고 있는다.
       */
      /*
       * 스마트 폴더 개수는 서버가 **첫 쪽에서만** 보낸다 (2026-08-22) — 쪽을 넘겨도
       * 값이 안 바뀌는데 다시 세는 것이 루트 조회 DB 시간의 대부분이었다.
       *
       * ⚠ 그래서 `?? []` 로 덮으면 안 된다. 2쪽으로 넘어가는 순간 좌측 목록과 목록 위
       *   출처 행이 통째로 사라진다. **필드가 실제로 왔을 때만** 갈아 끼운다.
       */
      if (atMyRoot && Array.isArray(res.smartFolders)) setSmartFolders(res.smartFolders);
    } catch (err) {
      /*
       * ★ 폴더 안으로 들어갔는데 404 면 «한 단계 위로» 되돌린다 (2026-10-27 사용자 지시).
       *   「공유된 폴더가 권한이 없어서 계속 누르면 내 파일 > {공유폴더} > {공유폴더} > … 이 계속 생긴다」.
       *   실패해도 목록은 앞 폴더 그대로라 같은 줄이 남고, 다시 누를 때마다 경로에 한 칸씩 붙었다.
       */
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404 && currentFolderId !== null && view !== 'trash' && !isStale()) {
        // 경로는 ref 로 읽는다 — 의존성에 넣으면 경로가 바뀔 때마다 목록을 다시 부른다.
        const path = breadcrumbRef.current;
        const at = path.findIndex((b) => b.id === currentFolderId);
        const back = at >= 0 ? path.slice(0, at) : path;
        setBreadcrumb(back);
        setCurrentFolderId(back[back.length - 1]?.id ?? null);
        setFolderDenied(tWorkspace('workspace.err.folder_denied'));
        return;
      }
      setError((err as Error)?.message ?? tWorkspace('workspace.err.fetch_failed'));
    } finally {
      setLoading(false);
    }
  }, [currentFolderId, query, kind, view, sortField, sortOrder, smartSource, atCompanyTop, page, pageSize]);

  /**
   * 목록을 바꾸는 동작 뒤에 쓰는 갱신 — 본문 표 **와 좌측 폴더 트리를 함께** 새로 읽는다.
   *
   * 트리는 한 번 읽고 캐시하므로, 표만 갱신하면 방금 만든 폴더가 좌측에 없다.
   */
  const refreshAll = useCallback(async () => {
    setTreeVersion((v) => v + 1);
    await refresh();
  }, [refresh]);

  const actions = useWorkspaceActions({ refresh: refreshAll, clearSelection });

  /** 선택된 노드들 — 목록에서 id 로 되찾는다. */
  /**
   * 화면이 다루는 행 전체 = 폴더 구획 + 이 쪽의 파일 (2026-08-21).
   *
   * 선택·상세·끌기·전체선택이 모두 이것을 본다 — 폴더만 따로 두면 폴더를 골랐을 때
   * 상세가 안 열리거나 전체선택이 폴더를 빠뜨린다. 표시 순서도 이 순서 그대로다.
   */
  const nodes = folderSection.length > 0 ? [...folderSection, ...children] : children;

  /**
   * 내 파일 루트인가 (2026-08-21).
   *
   * 서버가 `smartFolders` 를 싣는 조건(scope=folder · parentId=null · 검색 아님)과
   * **같은 판정**이다. 목록 위의 출처 행도 여기서만 그린다 — 폴더 안이나 검색 중에는
   * 전체 기준 묶음이라 "이 폴더 안의 채팅 첨부" 로 잘못 읽힌다.
   */
  const atMyRoot = view === 'my' && smartSource === null
    && currentFolderId === null && query === '';

  const selectedNodes = nodes.filter((c) => selected.has(c.id));
  /** 휴지통은 목록이 따로다(스키마가 다르다) — 같은 `selected` 집합을 저쪽에서 되찾는다. */
  const selectedTrash = trashItems.filter((t) => selected.has(t.id));
  const allTrashSelected = trashItems.length > 0 && trashItems.every((t) => selected.has(t.id));
  /**
   * 세부정보 패널의 대상 — **이름으로 연 것이 우선**이고, 없으면 체크가 정확히 하나일 때 그것.
   * 체크가 여러 개면 대상이 정해지지 않으므로 패널은 개수만 알린다(기존 동작 유지).
   */
  const detailNode = nodes.find((c) => c.id === focusedId)
    ?? (selectedNodes.length === 1 ? selectedNodes[0]! : null);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * 목록 정체성이 바뀌면 1쪽으로 되돌린다 (2026-08-21).
   *
   * 폴더를 옮기거나 검색·유형·정렬을 바꿨는데 5쪽에 머물러 있으면 화면이 빈 채로 뜬다.
   * 렌더 중에 되돌리는 것은 "다른 것에서 파생되는 상태" 의 표준 패턴이다 — effect 로
   * 하면 잘못된 쪽으로 한 번 조회하고 다시 조회해 **요청이 두 번** 나간다.
   */
  const listKey = [
    view, currentFolderId ?? '', query, kind, sortField ?? '', sortOrder, smartSource ?? '', pageSize,
  ].join('|');
  const listKeyRef = useRef(listKey);
  if (listKeyRef.current !== listKey) {
    listKeyRef.current = listKey;
    if (page !== 1) setPage(1);
  }

  // 폴더 이동 시 ④ 상세 선택 해제 — 선택 파일이 현재 폴더에 없을 수 있으므로.
  useEffect(() => {
    setSelectedFile(null);
  }, [currentFolderId]);

  function handleEnterFolder(folder: NodePublic) {
    setFolderDenied(null);
    /*
     * 이미 경로에 있는 폴더면 «붙이지 않고» 그 자리로 자른다 (2026-10-27) — 같은 폴더를 거듭 눌러도
     * `… > A > A > A` 가 되지 않는다. 지금 있는 폴더면 그대로다.
     */
    setBreadcrumb((prev) => {
      const at = prev.findIndex((b) => b.id === folder.id);
      return at >= 0 ? prev.slice(0, at + 1) : [...prev, folder];
    });
    setCurrentFolderId(folder.id);
  }

  function handleBreadcrumbJump(idx: number) {
    setFolderDenied(null);
    if (idx === -1) {
      // root
      setBreadcrumb([]);
      setCurrentFolderId(null);
    } else {
      setBreadcrumb((prev) => prev.slice(0, idx + 1));
      setCurrentFolderId(breadcrumb[idx]?.id ?? null);
    }
  }

  /**
   * 좌측 네비 전환. 화면이 바뀌면 폴더 위치·검색을 초기화한다 — 안 그러면
   * '공유됨' 에서 되돌아왔을 때 남의 폴더 경로가 브레드크럼에 남는다.
   */
  function handleViewChange(next: WorkspaceView) {
    setFolderDenied(null);
    setView(next);
    setSelectedFile(null);
    setQuery('');
    // 쪽 되돌리기는 위 `listKey` 가 맡는다 — 화면이 바뀌면 자동으로 1쪽이다.
    clearSelection();
    setFocusedId(null);
    setSmartSource(null);
    setBreadcrumb([]);
    setCurrentFolderId(null);
    /*
     * 공유일 축은 **공유됨 화면에서만 뜻이 있다** — 다른 화면으로 나가면 놓는다.
     * 안 놓으면 서버가 수정일로 떨어뜨려 처리하는데, 화면에는 정렬이 걸린 것처럼 보여
     * "왜 이 순서지" 가 된다. 놓은 자리는 화면 기본값(수정일 최신순)으로 되돌린다.
     */
    if (sortField === 'shared' && next !== 'shared' && next !== 'shared-by-me') {
      setSortField('updated');
      setSortOrder('desc');
    }
    /*
     * 회사 문서 = **전사 공유된 항목의 모음** (2026-08-14 2차).
     *
     * ⚠ 예전에는 여기서 `getCompanyFolder()` 를 await 했다. 화면 전환 한 번이 목록 조회와
     *   폴더 조회 두 개를 동시에 띄웠고, 그 둘의 도착 순서에 따라 목록이 보였다 안 보였다
     *   했다(사용자 보고). 이제 회사 문서에 폴더가 없으므로 전환은 **상태 변경뿐**이고
     *   조회는 목록 하나다 — 경합할 상대가 없다.
     *
     * 읽기 전용 여부도 역할로 바로 안다(서버가 준 canManage 와 같은 판정 —
     * workspace-access.isWorkspaceAdmin). 최종 차단은 여전히 서버가 한다.
     */
    setCompanyReadOnly(next === 'company' && !isAdmin);
  }

  /** 새 문서 만들기 — 이름을 묻고, 만들고, 곧바로 그 문서를 연다. */
  async function handleNewDocument(doc: NewDocKind) {
    const name = await layerPrompt(tWorkspace('workspace.create.name_prompt'), {
      defaultValue: tWorkspace('workspace.create.default_name'),
    });
    if (name === null || name.trim() === '') return;
    setLoading(true);
    try {
      const created = await createBlankDocument(doc, name, createParentId);
      await refreshAll();
      // 만든 직후 편집기로 넘긴다 — 만들어 놓고 목록에서 다시 찾게 하지 않는다.
      setSelectedFile({
        id: created.fileId,
        type: 'workspace-file',
        name: created.name,
        parentId: createParentId,
        uploadedBy: '',
        mimeType: null,
        sizeBytes: 0,
        verified: true,
        createdAt: new Date().toISOString(),
      });
    } catch (err) {
      const reason = (err as any)?.response?.data?.message ?? (err as Error).message;
      void layerAlert(tWorkspace('workspace.create.failed', { reason }));
    } finally {
      setLoading(false);
    }
  }

  /**
   * 행 메뉴 · 선택 툴바가 부르는 단일 진입점.
   *
   * 둘이 같은 동작을 서로 다르게 부르면 "행 메뉴로는 되는데 툴바로는 안 된다" 가 생긴다.
   * `nodes` 만 다르고 나머지는 같다.
   */
  async function runAction(action: RowAction | BulkAction, nodes: NodePublic[]) {
    if (nodes.length === 0) return;
    const first = nodes[0]!;
    switch (action) {
      case 'share':
        setShareTarget({ node: first, tab: 'add' }); return;
      case 'access':
        setShareTarget({ node: first, tab: 'manage' }); return;
      case 'download':
        await actions.download(nodes); return;
      case 'copy-link':
        await actions.copyLink(first); return;
      case 'rename':
        await actions.rename(first); return;
      case 'move':
        setPickerTarget({ nodes, mode: 'move' }); return;
      case 'copy-to':
        setPickerTarget({ nodes, mode: 'copy' }); return;
      case 'duplicate':
        await actions.duplicate(nodes); return;
      case 'color':
        setColorTarget(first); return;
      case 'detail':
        // 세부정보는 패널을 여는 것이 전부다. 행 메뉴에서 눌렀으면 그 항목을 대상으로 잡는다.
        // ⚠ 체크는 건드리지 않는다 — 체크는 일괄 처리용이라 뜻이 다르다.
        setFocusedId(first.id);
        setSelectedFile(null);
        setDetailOpen(true);
        return;
      case 'delete':
        await confirmDelete(nodes); return;
      case 'unshare':
        await unshareAll(first); return;
      case 'company-register':
        await registerToCompany(nodes); return;
      case 'company-unregister':
        await unregisterFromCompany(nodes); return;
      default:
        return;
    }
  }

  /**
   * 회사공유에 등록 (2026-08-14) — 관리자가 **자기 '내 파일' 에서** 전사 공개한다.
   *
   * 파일을 회사 폴더로 옮기지 않는다. 옮기면 원본이 내 파일에서 사라져 "공유했더니 내
   * 것이 없어졌다" 가 된다. 전사 공유 한 줄만 걸고, 회사공유 목록이 그것을 함께 보여 준다.
   */
  async function registerToCompany(nodes: NodePublic[]) {
    const label = nodes.length === 1
      ? tWorkspace('workspace.company.confirm_register', { name: nodes[0]!.name })
      : tWorkspace('workspace.company.confirm_register', {
        name: `${nodes[0]!.name} 외 ${nodes.length - 1}개`,
      });
    if (!(await layerConfirm(label))) return;
    await actions.runBatch(nodes, (n) => addShare(n.id, { targetType: 'company', permission: 'VIEW' }));
  }

  /** 회사공유에서 내리기 — 전사 공유만 푼다(팀·개인 공유는 그대로 둔다). */
  async function unregisterFromCompany(nodes: NodePublic[]) {
    if (!(await layerConfirm(tWorkspace('workspace.company.confirm_unregister', { name: nodes[0]!.name })))) return;
    await actions.runBatch(nodes, (n) => removeShare(n.id, 'company', '0'));
  }

  /**
   * 삭제 전 사용처 확인.
   *
   * 워크스페이스에서 지우면 그 파일을 첨부한 채팅 메시지의 첨부가 **조용히 404** 가 된다
   * (다운로드 가드가 `deletedAt IS NULL` 을 요구한다). 무엇이 깨지는지 먼저 보여 준다.
   * 참조가 하나도 없으면 예전처럼 간단한 확인만 받는다 — 매번 무거운 모달을 띄우면
   * 경고가 소음이 되어 읽지 않게 된다.
   */
  async function confirmDelete(nodes: NodePublic[]) {
    const reports = await Promise.all(nodes.map(async (node) => {
      // 폴더는 첨부로 참조될 수 없다 — 조회를 아낀다.
      if (node.type === 'folder') return { node, usage: null };
      try {
        return { node, usage: await getFileUsage(node.id) };
      } catch {
        // 사용처를 못 읽었다고 삭제를 막지는 않는다. 다만 경고 없이 진행한다.
        return { node, usage: null };
      }
    }));

    const targets = reports.filter((r) => (r.usage?.total ?? 0) > 0) as DeleteTarget[];
    const clean = reports.filter((r) => (r.usage?.total ?? 0) === 0).map((r) => r.node);

    if (targets.length === 0) {
      await actions.remove(nodes);
      return;
    }
    setDeleteWarn({ targets, clean });
  }

  /** '내가 공유함' 목록에서 바로 공유를 전부 해제한다. */
  async function unshareAll(node: NodePublic) {
    if (!(await layerConfirm(tWorkspace('workspace.row.confirm_unshare', { name: node.name })))) return;
    try {
      const { shares } = await listShares(node.id);
      for (const s of shares) await removeShare(node.id, s.targetType, s.targetId);
      await refresh();
    } catch (err) {
      void layerAlert((err as any)?.response?.data?.message ?? (err as Error).message);
    }
  }

  /**
   * 컬럼 머리글 클릭 정렬 (2026-08-14 2차 사용자 요구).
   *
   * 같은 축을 다시 누르면 방향만 뒤집는다(표의 관례). 다른 축으로 갈아탈 때는 오름차순부터 —
   * 앞 축의 방향을 물려받으면 "이름을 눌렀는데 왜 거꾸로지" 가 된다.
   *
   * ⚠ 페이지네이션 커서는 정렬 유무로 해석이 갈리므로(서버가 offset 으로 본다) 축을 바꾸면
   *   **커서를 버린다**. 안 버리면 2페이지가 새 정렬의 앞부분을 건너뛴 채 붙는다.
   */
  function handleColumnSort(field: SortField) {
    if (sortField === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    // 축을 바꾸면 1쪽으로 — `listKey` 가 정렬 축·방향을 포함한다.
  }

  function handleCreateAction(action: CreateAction) {
    if (action.kind === 'folder') { setCreating(true); return; }
    if (action.kind === 'upload-files') { setUploadDirectory(false); setUploading(true); return; }
    if (action.kind === 'upload-folder') { setUploadDirectory(true); setUploading(true); return; }
    void handleNewDocument(action.doc);
  }

  async function handleRestore(node: TrashNode) {
    try {
      const res = await restoreFromTrash(node.id);
      if (res.movedToRoot) void layerAlert(tWorkspace('workspace.trash.restored_to_root'));
      await refreshAll();
    } catch (err) {
      void layerAlert((err as any)?.response?.data?.message ?? (err as Error).message);
    }
  }

  async function handlePurge(node: TrashNode) {
    if (!(await layerConfirm(tWorkspace('workspace.trash.confirm_purge', { name: node.name })))) return;
    try {
      await purgeFromTrash(node.id);
      await refreshAll();
    } catch (err) {
      void layerAlert((err as any)?.response?.data?.message ?? (err as Error).message);
    }
  }

  /**
   * 휴지통 일괄 처리 (2026-08-14) — 체크한 것만.
   *
   * 하나가 실패해도 나머지는 계속한다. 첫 실패에서 멈추면 **어디까지 처리됐는지 알 수 없고**
   * 다시 눌러야 하는데, 이미 처리된 것에 또 요청이 간다. 끝나고 실패 개수만 알린다.
   */
  async function runTrashBulk(
    items: TrashNode[],
    op: (id: string) => Promise<unknown>,
  ) {
    let failed = 0;
    for (const item of items) {
      try {
        await op(item.id);
      } catch {
        failed += 1;
      }
    }
    clearSelection();
    await refreshAll();
    if (failed > 0) void layerAlert(tWorkspace('workspace.trash.bulk_failed', { count: failed }));
  }

  async function restoreSelected() {
    const items = selectedTrash;
    if (items.length === 0) return;
    await runTrashBulk(items, (id) => restoreFromTrash(id));
  }

  async function purgeSelected() {
    const items = selectedTrash;
    if (items.length === 0) return;
    // 영구 삭제는 되돌릴 수 없다 — 개수를 보여 주고 한 번 더 묻는다.
    if (!(await layerConfirm(tWorkspace('workspace.trash.confirm_purge_many', { count: items.length })))) return;
    await runTrashBulk(items, (id) => purgeFromTrash(id));
  }

  async function handleEmptyTrash() {
    if (!(await layerConfirm(tWorkspace('workspace.trash.confirm_empty')))) return;
    try {
      await emptyTrash();
      await refreshAll();
    } catch (err) {
      void layerAlert((err as any)?.response?.data?.message ?? (err as Error).message);
    }
  }

  return (
    <div className="workspace-page">
      {/*
        ⚠ 상단의 제목·설명 헤더와 스토리지 카드는 **의도적으로 없다** (2026-08-14, 시안 반영).
        둘이 세로 250px 가까이 먹어 정작 목록이 한 화면에 몇 줄 안 들어왔다.
        - 만들기·업로드 → 좌측 `＋ 만들기 또는 업로드` 가 이미 같은 일을 한다(폴더·파일·폴더업로드).
        - 스토리지 사용량 → 좌측 하단 고정 영역(ws-side-foot)에 그대로 남아 있다.
        되살릴 일이 생기면 지우는 것보다 좌측으로 옮기는 쪽을 먼저 검토할 것.
      */}
      <div className="workspace-body">
        {/* ★ 접기는 **삭제했다**(2026-09-08 사용자 지시) — 좌측은 늘 서 있다. */}
        {(
        <WorkspaceSidebar
          view={view}
          onViewChange={handleViewChange}
          activeFolderId={currentFolderId}
          treeVersion={treeVersion}
          // 2026-10-27 — 지금 있는 폴더까지 펼쳐 다시 읽는다(방금 만든 하위 폴더가 바로 보이게).
          treeRevealIds={breadcrumb.map((b) => b.id)}
          usage={usage}
          smartFolders={smartFolders}
          smartSource={smartSource}
          onSmartSelect={(source) => {
            setSmartSource(source);
            clearSelection();
          }}
          canCreate={!companyReadOnly}
          createDisabledHint={tWorkspace('workspace.company.read_only_hint')}
          onCreateAction={handleCreateAction}
          onManageStorage={() => setStorageOpen(true)}
          // 2026-10-15 — 「내 파일」 폴더 줄 끝 「…」 — 본문 목록의 ⋮ 와 같은 메뉴 · 같은 처리(runAction).
          onFolderMenu={(node, anchor) => setRowMenu({ node, anchor })}
          onFolderSelect={(folder) => {
            // tree 클릭 시 breadcrumb 재구성 — 단순화: 하위 → 직접 점프 + breadcrumb [folder] 만.
            if (folder === null) {
              setBreadcrumb([]);
              setCurrentFolderId(null);
            } else {
              setBreadcrumb([folder]);
              setCurrentFolderId(folder.id);
            }
          }}
          /*
            좌측 트리에 끌어다 놓기 (2026-08-18) — 목록에서 끌어온 것을 트리의 폴더(또는
            루트)로 옮긴다. 짐은 `dataTransfer` 에 id 로 실려 오므로 지금 목록에서 실제
            노드를 되찾는다(트리는 목록을 모른다). 읽기 전용이면 아예 주지 않는다.
          */
          onFolderDropMove={companyReadOnly ? undefined : (nodeIds, folderId) => {
            const moving = nodes.filter((c) => nodeIds.includes(c.id));
            if (moving.length === 0) return;
            void actions.moveTo(moving, folderId);
          }}
          onFolderDropFiles={canUpload
            ? (folderId, folderName, files) => openUploadWithFiles(files, { id: folderId, name: folderName })
            : undefined}
        />
        )}

        <main
          className={`workspace-main${fileOver ? ' workspace-main--fileover' : ''}`}
          onDragOver={(e) => {
            if (!canUpload || !hasExternalFiles(e.dataTransfer)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
            if (!fileOver) setFileOver(true);
          }}
          onDragLeave={(e) => {
            // 자식으로 옮겨가는 중에는 끄지 않는다 — 깜빡임의 원인.
            if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
            setFileOver(false);
          }}
          onDrop={(e) => {
            if (!canUpload || !hasExternalFiles(e.dataTransfer)) return;
            e.preventDefault();
            setFileOver(false);
            openUploadWithFiles(readExternalFiles(e.dataTransfer));
          }}
        >
          {fileOver && (
            <div className="workspace-dropveil" aria-hidden>
              <span>{tWorkspace('workspace.upload.drop_here')}</span>
            </div>
          )}
          {/*
            ★★★ 화면 머리 — **정본 `PageHeader`(A3)** (2026-09-08 사용자 지적).
               근태 · 메일에는 있는 **경로 · 제목 · 한 줄 설명 · 도움말**이 여기만 통째로 없었다.
            ★ 아래 브레드크럼(뿌리 › 폴더 › 폴더)은 **폴더 길**이라 다른 물건이다 —
              도구줄에 그대로 둔다. 여기 경로는 «도메인 안에서 어느 화면인가» 다.
          */}
          <PageHeader
            className="ws-head"
            breadcrumb={[tWorkspace('workspace.title'), tWorkspace(ROOT_LABEL_KEY[view])]}
            title={tWorkspace(ROOT_LABEL_KEY[view])}
            description={tWorkspace(VIEW_DESC_KEY[view])}
            help={{
              title: tWorkspace('workspace.head.help_title'),
              body: (
                <>
                  <p>{tWorkspace('workspace.head.help_drag')}</p>
                  <p>{tWorkspace('workspace.head.help_share')}</p>
                  <p>{tWorkspace('workspace.head.help_trash')}</p>
                </>
              ),
            }}
          />
          {/*
            선택이 있으면 브레드크럼 줄을 **툴바로 대체**한다(위에 겹치지 않는다) —
            겹치면 좁은 폭에서 두 줄이 되어 목록이 그만큼 밀린다.
          */}
          {selected.size > 0 && view === 'trash' ? (
            <WorkspaceTrashBar
              count={selected.size}
              onClear={clearSelection}
              onRestore={() => { void restoreSelected(); }}
              onPurge={() => { void purgeSelected(); }}
            />
          ) : selected.size > 0 ? (
            <WorkspaceSelectionBar
              count={selected.size}
              detailOpen={detailOpen}
              readOnly={companyReadOnly}
              onClear={clearSelection}
              onAction={(a) => { void runAction(a, selectedNodes); }}
              onSort={(el) => setSortAnchor(el)}
              onView={(el) => setViewAnchor(el)}
            />
          ) : (
            <div className="workspace-toolbar">
              <nav className="workspace-breadcrumb" aria-label={tWorkspace('workspace.aria.breadcrumb')}>
                {/*
                  뿌리 이름은 **지금 있는 화면**이다 (2026-08-14 2차).
                  전에는 어느 화면에서나 '워크스페이스' 였다 — 회사 문서에서 폴더로 들어가면
                  `워크스페이스 > 규정` 이 되어, 그 폴더가 내 파일에 있는 것처럼 읽혔다.
                  누르면 그 화면의 최상위로 돌아가므로 이름도 그 화면이어야 맞다.
                */}
                <button type="button" onClick={() => handleBreadcrumbJump(-1)}>
                  {tWorkspace(ROOT_LABEL_KEY[view])}
                </button>
                {breadcrumb.map((b, i) => (
                  <span key={b.id}>
                    <ChevronRight size={12} aria-hidden />
                    <button type="button" onClick={() => handleBreadcrumbJump(i)}>{b.name}</button>
                  </span>
                ))}
              </nav>

              <span className="ws-toolbar-sp" />

              {/* 왜 버튼이 없는지 알려 주는 유일한 자리 — 감추기만 하면 고장으로 읽힌다. */}
              {companyReadOnly && (
                <span className="ws-readonly-pill" title={tWorkspace('workspace.company.read_only_hint')}>
                  <Lock size={12} aria-hidden />
                  {tWorkspace('workspace.company.read_only')}
                </span>
              )}

              {view === 'trash' ? (
                <button type="button" className="workspace-view-button" onClick={() => void handleEmptyTrash()}>
                  <Trash2 size={14} /> {tWorkspace('workspace.trash.empty_all')}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="workspace-view-button"
                    onClick={(e) => setSortAnchor(e.currentTarget)}
                  >
                    <ArrowUpDown size={14} /> {tWorkspace('workspace.toolbar.sort')}
                  </button>
                  <button
                    type="button"
                    className="workspace-view-button"
                    onClick={(e) => setViewAnchor(e.currentTarget)}
                  >
                    <ListIcon size={14} /> {tWorkspace('workspace.toolbar.view')}
                  </button>
                  <button
                    type="button"
                    className={`workspace-view-button${detailOpen ? ' workspace-view-button--active' : ''}`}
                    onClick={() => setDetailOpen((v) => !v)}
                  >
                    <Info size={14} /> {tWorkspace('workspace.toolbar.detail')}
                  </button>
                </>
              )}
            </div>
          )}

          {/*
            ★ 목록 줄 (2026-10-15 사용자 요구) — 우측 패널(세부정보·미리보기)을 «도구줄 아래»에서
              목록과 같은 높이로 세운다. 전에는 패널이 `<main>` 밖 형제라 화면 맨 위(제목 옆)부터
              바닥까지 섰다 — 「세부정보」 단추를 눌렀는데 그 단추보다 위에서 열렸다.
              ⇒ 걸러 보기·목록·페이저를 왼쪽 칸에, 패널을 오른쪽 칸에 둔다.
          */}
          <div className="ws-split">
          <div className="ws-split__list">
          {/*
            검색·유형 칩 (2026-08-12 도입 → 2026-08-14 시안 위치로 이동).
            예전에는 좌측 메뉴 **위** 전체 폭에 있었다. 시안은 목록과 같은 칸에서
            브레드크럼 바로 아래에 둔다 — 걸러지는 대상 바로 위에 있어야 무엇을 거르는지가 분명하다.
          */}
          <WorkspaceFilterBar
            kind={kind}
            onKindChange={setKind}
            query={query}
            onQueryChange={setQuery}
            searching={searching}
          />

          {folderDenied && !error && (
            <div className="workspace-error" role="alert">{folderDenied}</div>
          )}
          {error && (
            <div className="workspace-error" role="alert">
              {error}
              <button type="button" onClick={refresh}>{tWorkspace('workspace.btn.retry')}</button>
            </div>
          )}

          {/* 휴지통은 목록과 스키마가 달라(삭제 시각·복원/영구삭제) 자체 표로 그린다. */}
          {view === 'trash' && (
            <ul
              className="ws-trash-list"
              /* 끌기 뒤에 따라오는 click 한 번을 삼킨다 — 표와 같은 규칙. */
              onClickCapture={(e) => {
                if (!trashDrag.consumeClick()) return;
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              {trashItems.length === 0 && !loading && (
                <li className="workspace-grid-empty">{tWorkspace('workspace.view.empty')}</li>
              )}
              {/* 전체 선택 — 표(WorkspaceTable)의 머리줄과 같은 규칙. */}
              {trashItems.length > 0 && (
                <li className="ws-trash-row ws-trash-head">
                  <button
                    type="button"
                    className={`ws-check${allTrashSelected ? ' ws-check--on' : ''}`}
                    role="checkbox"
                    aria-checked={allTrashSelected}
                    aria-label={tWorkspace('workspace.row.aria_select_all')}
                    onClick={() => setSelected(allTrashSelected
                      ? new Set()
                      : new Set(trashItems.map((t) => t.id)))}
                  >
                    <Check size={11} aria-hidden />
                  </button>
                  <span className="ws-trash-name">{tWorkspace('workspace.col.name')}</span>
                  <span className="ws-trash-date">{tWorkspace('workspace.trash.deleted_at')}</span>
                </li>
              )}
              {trashItems.map((item) => (
                // 끌려 지나가는 대상은 행 전체 — 상자에만 달면 빨리 끌 때 행이 건너뛰어진다.
                <li key={item.id} className="ws-trash-row" {...trashDrag.targetProps(item.id)}>
                  <button
                    type="button"
                    className={`ws-check${selected.has(item.id) ? ' ws-check--on' : ''}`}
                    role="checkbox"
                    aria-checked={selected.has(item.id)}
                    aria-label={tWorkspace('workspace.row.aria_select', { name: item.name })}
                    {...trashDrag.startProps(item.id, selected.has(item.id))}
                    onClick={() => setSelected((cur) => {
                      const next = new Set(cur);
                      next.has(item.id) ? next.delete(item.id) : next.add(item.id);
                      return next;
                    })}
                  >
                    <Check size={11} aria-hidden />
                  </button>
                  <WorkspaceFileIcon node={item} size={18} />
                  <span className="ws-trash-name" title={item.name}>{item.name}</span>
                  <span className="ws-trash-date ws-num">
                    {item.deletedAt ? item.deletedAt.slice(0, 16).replace('T', ' ') : '—'}
                  </span>
                  <button type="button" className="ws-trash-act" onClick={() => void handleRestore(item)}>
                    <RotateCcw size={13} aria-hidden /> {tWorkspace('workspace.trash.restore')}
                  </button>
                  <button
                    type="button"
                    className="ws-trash-act ws-trash-act--danger"
                    onClick={() => void handlePurge(item)}
                  >
                    <Trash2 size={13} aria-hidden /> {tWorkspace('workspace.trash.purge')}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/*
            2026-08-14 — 출처별 모음 칩 줄은 **좌측 네비('내 파일' 하위)로 옮겼다**.
            목록 위 칩은 세로를 먹었고, 출처를 고르면 서버가 목록을 안 줘서 칩 자체가 사라졌다.
          */}
          {view !== 'trash' && (
            <WorkspaceTable
              nodes={nodes}
              loading={loading}
              view={tableView}
              selected={selected}
              focusedId={focusedId}
              dateLabelKey={
                view === 'shared' || view === 'shared-by-me'
                  ? 'workspace.col.shared_at' : 'workspace.col.modified'
              }
              // 회사공유가 비었을 때 "폴더가 비어 있다" 는 상황을 잘못 설명한다.
              /*
                '내 파일' 루트가 비면 **어디로 갔는지** 적는다 (2026-09-02). 이 화면이
                워크스페이스에 둔 것만 보여 주도록 바뀌어, 첨부만 있던 사람은 어느 날
                갑자기 목록이 텅 빈다 — 「표시할 항목이 없습니다」만 놓으면 파일이 지워진
                줄 안다.
              */
              emptyKey={atCompanyTop ? 'workspace.company.empty'
                : (view === 'my' && currentFolderId === null && !query
                  ? 'workspace.view.empty_my_root' : 'workspace.view.empty')}
              // 컬럼 머리글 클릭 정렬 — 상단 '정렬' 메뉴와 **같은 상태**를 공유한다.
              // 둘이 따로 놀면 메뉴에는 '이름순' 인데 표에는 날짜 화살표가 서는 일이 생긴다.
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={handleColumnSort}
              // 공유됨 화면의 날짜 열은 '공유일' 이다 — 축도 그것이어야 라벨과 결과가 맞는다.
              dateSortField={view === 'shared' || view === 'shared-by-me' ? 'shared' : 'updated'}
              /*
               * 출처별 모음을 목록 맨 위에 폴더처럼 (2026-08-21).
               *
               * 내 파일 루트의 98%가 다른 화면에서 흘러든 첨부라(운영 351건 중 직접 올린 것
               * 6건), 루트를 열면 남의 화면 첨부가 첫 화면을 채운다. 파일을 실제로 옮기는
               * 대신 **이미 서버가 세고 있는 출처별 목록**을 폴더처럼 보여 준다 —
               * 데이터는 한 글자도 안 바뀌고, 한 파일이 두 출처에 걸리면 양쪽에 다 보인다.
               */
              sources={atMyRoot ? smartFolders : undefined}
              onOpenSource={(source) => { setSmartSource(source); clearSelection(); setFocusedId(null); }}
              // '내가 공유함' 은 마지막 열이 공개여부가 아니라 **공유 대상**이다.
              personColumn={view === 'shared-by-me' ? {
                labelKey: 'workspace.col.share_target',
                nameOf: (n) => {
                  const s = n.shareSummary;
                  if (!s || s.targetCount === 0) return '—';
                  if (s.targetCount === 1) return s.firstTargetName ?? '—';
                  return tWorkspace('workspace.share.target_more', {
                    name: s.firstTargetName ?? '', count: s.targetCount - 1,
                  });
                },
              } : undefined}
              onToggleSelect={(id) => setSelected((cur) => {
                const next = new Set(cur);
                next.has(id) ? next.delete(id) : next.add(id);
                return next;
              })}
              // 끌어서 여러 개 켜고 끄기 (2026-08-18) — 체크 칸에서 눌러 아래로 훑는다.
              onSetSelect={setSelectOne}
              /*
                끌어다 폴더에 놓아 옮기기 (2026-08-18). 읽기 전용(회사공유 열람)일 때는
                아예 주지 않는다 — 못 하는 동작을 끌리게 두면 놓은 뒤에야 실패한다.
                옮기고 나면 runBatch 가 목록 갱신·선택 해제까지 한다(메뉴로 옮길 때와 같다).
              */
              onDropFiles={canUpload
                ? (folderId, folderName, files) => openUploadWithFiles(files, { id: folderId, name: folderName })
                : undefined}
              onDropMove={companyReadOnly ? undefined : (movingNodes, target) => {
                void actions.moveTo(movingNodes, target.id);
              }}
              onToggleSelectAll={() => setSelected((cur) => (
                cur.size === nodes.length ? new Set() : new Set(nodes.map((c) => c.id))
              ))}
              onOpen={handleEnterFolder}
              // 미리보기는 호버 아이콘을 눌렀을 때만 — 훑어보려고 누른 것뿐인데
              // 매번 무거운 렌더러가 뜨는 것을 막는다(사용자 요구).
              // 미리보기는 **먼저 인라인(세부정보와 같은 자리)** 으로 연다. 전체는 ⤢ 로
              // 넓혀서 본다 — 한 번 누른 것뿐인데 화면을 통째로 덮으면 목록으로 돌아가기 어렵다.
              onPreview={(node) => setSelectedFile(node)}
              onSelect={(node) => {
                // ⚠ 체크(selected)는 건드리지 않는다 — 이름 클릭은 "하나를 본다" 이지
                //   "여러 개를 골라 처리한다" 가 아니다. 체크는 체크박스로만 바뀐다.
                setFocusedId(node.id);
                setDetailOpen(true);
                /*
                 * ⚠ 열려 있던 미리보기를 **닫는다** (2026-08-14 교정).
                 *   우측 자리는 하나이고 문서 패널이 세부정보보다 우선한다. 그래서 한 번
                 *   미리보기를 열면 다른 파일을 눌러도 계속 앞 파일의 미리보기만 보였다 —
                 *   "파일명을 클릭하면 정보가 보여야 한다" 는 요구가 막혀 있었다.
                 *   미리보기는 이름 끝의 아이콘으로만 연다(그때는 onPreview 가 다시 채운다).
                 */
                setSelectedFile(null);
              }}
              onRowMenu={(node, anchor) => setRowMenu({ node, anchor })}
            />
          )}

          {/* 검색 결과가 0건일 때는 "폴더가 비어 있다" 가 아니라 검색 문구를 보여준다. */}
          {searching && !loading && nodes.length === 0 && (
            <div className="workspace-grid-empty">{tWorkspace('workspace.search.empty', { q: query })}</div>
          )}

          {/*
            번호 페이저 (2026-08-21) — '더 보기' 를 대체한다. 휴지통은 서버가 페이지를
            나누지 않아(한 번에 다 준다) 그리지 않는다.
          */}
          {view !== 'trash' && !loading && (
            <WorkspacePagination
              page={page}
              pageSize={pageSize}
              total={total}
              disabled={loading}
              onPage={(n) => { setPage(n); clearSelection(); setFocusedId(null); }}
              onPageSize={(n) => setPageSize(n)}
            />
          )}
          </div>
        {/*
          우측은 자리가 하나다 — 문서를 연 상태가 세부정보보다 우선한다.
          둘을 동시에 띄우면 목록이 밀려 표가 못 읽을 만큼 좁아진다.
        */}
        {selectedFile ? (
          <div className="workspace-detail">
            <WorkspaceDocPanel
              node={selectedFile}
              onClose={() => setSelectedFile(null)}
              onSaved={() => { void refresh(); }}
              readOnly={companyReadOnly}
            />
          </div>
        ) : detailOpen && view !== 'trash' ? (
          <WorkspaceDetailPanel
            node={detailNode}
            selectedCount={selected.size}
            onClose={() => setDetailOpen(false)}
            onManageAccess={(node) => setShareTarget({ node, tab: 'manage' })}
          />
        ) : null}
          </div>
        </main>
      </div>

      {/* ── 팝오버 ─────────────────────────────────────────────────────── */}
      {rowMenu && (
        <WorkspaceRowMenu
          node={rowMenu.node}
          anchor={rowMenu.anchor}
          // 목록 기준 근사 — 최종 판정은 서버가 한다(권한 없으면 404/403).
          canManage={!companyReadOnly}
          readOnly={companyReadOnly}
          showUnshare={view === 'shared-by-me'}
          // 회사공유 등록은 관리자만. 이미 등록된 것은 '등록' 대신 '내리기' 를 보여 준다.
          showCompanyRegister={isAdmin && !rowMenu.node.shareSummary?.hasCompany}
          showCompanyUnregister={isAdmin && Boolean(rowMenu.node.shareSummary?.hasCompany)}
          onAction={(a) => { void runAction(a, [rowMenu.node]); }}
          onClose={() => setRowMenu(null)}
        />
      )}

      {sortAnchor && (
        <WorkspaceSortMenu
          anchor={sortAnchor}
          field={sortField ?? 'name'}
          order={sortOrder}
          onChange={({ field, order }) => { setSortField(field); setSortOrder(order); }}
          onClose={() => setSortAnchor(null)}
        />
      )}

      {viewAnchor && (
        <WorkspacePopover
          anchor={viewAnchor}
          onClose={() => setViewAnchor(null)}
          align="right"
          width={188}
          ariaLabel={tWorkspace('workspace.view.aria')}
        >
          <PopoverItem
            icon={ListIcon} label={tWorkspace('workspace.view.list')}
            checked={tableView === 'list'}
            onClick={() => { setTableView('list'); setViewAnchor(null); }}
          />
          <PopoverItem
            icon={Rows} label={tWorkspace('workspace.view.compact')}
            checked={tableView === 'compact'}
            onClick={() => { setTableView('compact'); setViewAnchor(null); }}
          />
          <PopoverItem
            icon={LayoutGrid} label={tWorkspace('workspace.view.tile')}
            checked={tableView === 'tile'}
            onClick={() => { setTableView('tile'); setViewAnchor(null); }}
          />
        </WorkspacePopover>
      )}

      {/* ── 모달 ───────────────────────────────────────────────────────── */}
      {shareTarget && (
        <WorkspaceShareModal
          node={shareTarget.node}
          initialTab={shareTarget.tab}
          onClose={() => setShareTarget(null)}
          onChanged={() => { void refresh(); }}
        />
      )}

      {pickerTarget && (
        <WorkspaceFolderPicker
          moving={pickerTarget.nodes}
          mode={pickerTarget.mode}
          onClose={() => setPickerTarget(null)}
          onPick={async (parentId) => {
            if (pickerTarget.mode === 'move') await actions.moveTo(pickerTarget.nodes, parentId);
            else await actions.copyTo(pickerTarget.nodes, parentId);
            setPickerTarget(null);
          }}
        />
      )}

      {deleteWarn && (
        <WorkspaceDeleteWarnModal
          targets={deleteWarn.targets}
          cleanCount={deleteWarn.clean.length}
          onCancel={() => setDeleteWarn(null)}
          onConfirm={() => {
            const all = [...deleteWarn.targets.map((t) => t.node), ...deleteWarn.clean];
            setDeleteWarn(null);
            // 경고를 이미 봤으므로 여기서 또 묻지 않는다 — 확인을 두 번 받으면 읽지 않게 된다.
            void actions.removeConfirmed(all);
          }}
        />
      )}

      {colorTarget && (
        <WorkspaceColorModal
          node={colorTarget}
          onClose={() => setColorTarget(null)}
          onApply={async (color) => {
            await actions.setColor(colorTarget, color);
            setColorTarget(null);
          }}
        />
      )}

      {shouldRenderCreate && (
        <FolderCreateModal
          parentId={createParentId}
          parentName={createParentName}
          isLeaving={isCreateLeaving}
          onClose={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false);
            await refreshAll();
          }}
        />
      )}

      {storageOpen && (
        <StorageManageModal
          onClose={() => setStorageOpen(false)}
          // 비우면 목록·사용량·좌측 트리가 전부 달라진다 — 셋을 한 번에 다시 읽는다.
          onChanged={() => { void refreshAll(); }}
        />
      )}

      {shouldRenderUpload && (
        <FileUploadModal
          parentId={uploadTarget ? uploadTarget.id : createParentId}
          parentName={uploadTarget ? uploadTarget.name : createParentName}
          isLeaving={isUploadLeaving}
          directory={uploadDirectory}
          initialFiles={dropFiles}
          onClose={() => { setUploading(false); setDropFiles([]); setUploadTarget(null); }}
          // 모달을 닫지 않는다 — 여러 개를 올린 결과(성공/실패)를 사용자가 확인하고 닫게 한다.
          onUploaded={() => { void refreshAll(); }}
        />
      )}
    </div>
  );
}
